// Shared music card components. Used by:
//   - Builder MusicStep (owner-side) — shows the connected user's top
//     tracks + artists alongside the connect/refresh controls.
//   - PublicProfile — shows the same data to anyone viewing /p/:slug.
//
// One file so the two surfaces stay visually identical; if we tweak
// padding or font sizing here, both update.

import { useEffect, useRef, useState } from 'react';
import { SpotifyTrack, SpotifyArtist } from '@/lib/api';
import './MusicCards.css';

// Small Spotify mark inline-SVG. Pure stroke, currentColor-aware via
// the parent's color CSS variable.
function SpotifyMark({ size = 14 }: { size?: number }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      width={size}
      height={size}
      fill="currentColor"
      aria-hidden="true"
    >
      <path d="M12 0C5.4 0 0 5.4 0 12s5.4 12 12 12 12-5.4 12-12S18.66 0 12 0zm5.521 17.34c-.24.359-.66.48-1.021.24-2.82-1.74-6.36-2.101-10.561-1.141-.418.122-.779-.179-.899-.539-.12-.421.18-.78.54-.9 4.56-1.021 8.52-.6 11.64 1.32.42.18.479.659.301 1.02zm1.44-3.3c-.301.42-.841.6-1.262.3-3.239-1.98-8.159-2.58-11.939-1.38-.479.12-1.02-.12-1.14-.6-.12-.48.12-1.021.6-1.141C9.6 9.9 15 10.561 18.72 12.84c.361.181.54.78.241 1.2zm.12-3.36C15.24 8.4 8.82 8.16 5.16 9.301c-.6.179-1.2-.181-1.38-.721-.18-.601.18-1.2.72-1.381 4.26-1.26 11.28-1.02 15.721 1.621.539.3.719 1.02.419 1.56-.299.421-1.02.599-1.559.3z" />
    </svg>
  );
}

// MarqueeText only enables the marquee animation when its content is
// actually wider than its container, AND animates by the exact pixel
// overflow amount so the text scrolls visibly from start to end. The
// previous CSS-only approach used `text-indent: -100%` which is
// relative to container width — not text-overflow distance — so when
// the text overflowed by more than the container width, the visible
// motion was a tiny shake instead of a proper scroll.
//
// Re-checks on:
//   - mount / children change (font might not have loaded yet on first
//     measure; the ResizeObserver fires once paint completes)
//   - container resize (ResizeObserver — handles viewport changes +
//     parent reflow)
function MarqueeText({
  children,
  className,
}: {
  children: string;
  className: string;
}) {
  const ref = useRef<HTMLSpanElement>(null);
  const [overflowPx, setOverflowPx] = useState(0);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const check = () => {
      // +1 absorbs sub-pixel rounding noise that can flip-flop the
      // class on text that exactly fits.
      const diff = el.scrollWidth - el.clientWidth;
      setOverflowPx(diff > 1 ? diff : 0);
    };
    check();
    const ro = new ResizeObserver(check);
    ro.observe(el);
    return () => ro.disconnect();
  }, [children]);

  const overflowing = overflowPx > 0;
  // CSS var carries the exact negative pixel offset the keyframes
  // animate to. Only set when overflowing — otherwise no inline style.
  const style: React.CSSProperties | undefined = overflowing
    ? ({ ['--mc-overflow' as never]: `-${overflowPx}px` } as React.CSSProperties)
    : undefined;
  return (
    <span
      ref={ref}
      className={overflowing ? `${className} mc-overflowing` : className}
      title={children}
      style={style}
    >
      {children}
    </span>
  );
}

function TrackCard({ track }: { track: SpotifyTrack }) {
  return (
    <a
      className="mc-card"
      href={track.external_url || '#'}
      target={track.external_url ? '_blank' : undefined}
      rel="noreferrer"
    >
      <span className="mc-spotify-logo" title="From Spotify"><SpotifyMark /></span>
      {track.image_url ? (
        <img className="mc-thumb mc-thumb-square" src={track.image_url} alt="" />
      ) : (
        <div className="mc-thumb mc-thumb-square" style={{ background: 'var(--chip)' }} />
      )}
      <div className="mc-text">
        <MarqueeText className="mc-title">{track.name}</MarqueeText>
        <MarqueeText className="mc-sub">{track.artist}</MarqueeText>
      </div>
    </a>
  );
}

function ArtistCard({ artist }: { artist: SpotifyArtist }) {
  const genres = artist.genres?.slice(0, 2).join(' · ') ?? '';
  return (
    <a
      className="mc-card"
      href={artist.external_url || '#'}
      target={artist.external_url ? '_blank' : undefined}
      rel="noreferrer"
    >
      <span className="mc-spotify-logo" title="From Spotify"><SpotifyMark /></span>
      {artist.image_url ? (
        <img className="mc-thumb mc-thumb-round" src={artist.image_url} alt="" />
      ) : (
        <div className="mc-thumb mc-thumb-round" style={{ background: 'var(--chip)' }} />
      )}
      <div className="mc-text">
        <MarqueeText className="mc-title">{artist.name}</MarqueeText>
        {genres && <MarqueeText className="mc-sub">{genres}</MarqueeText>}
      </div>
    </a>
  );
}

interface MusicSplitProps {
  tracks: SpotifyTrack[];
  artists: SpotifyArtist[];
  // Headers — different copy on builder ("Top tracks · 4 wks") vs public
  // profile ("On repeat") so we pass them in rather than hardcode.
  tracksLabel?: string;
  artistsLabel?: string;
  // Empty-state copy. Optional — when omitted, the empty column simply
  // doesn't render (used on the public profile where we hide empties).
  tracksEmpty?: string;
  artistsEmpty?: string;
}

// MusicSplit is the side-by-side layout. Stacks at < 480px wide,
// two-column above. Used in both Builder + PublicProfile.
export function MusicSplit({
  tracks,
  artists,
  tracksLabel = 'Top tracks',
  artistsLabel = 'Top artists',
  tracksEmpty,
  artistsEmpty,
}: MusicSplitProps) {
  const tracksCol = tracks.length > 0
    ? <div className="mc-list">{tracks.map((t) => <TrackCard key={t.id} track={t} />)}</div>
    : tracksEmpty
      ? <EmptyNote>{tracksEmpty}</EmptyNote>
      : null;

  const artistsCol = artists.length > 0
    ? <div className="mc-list">{artists.map((a) => <ArtistCard key={a.id} artist={a} />)}</div>
    : artistsEmpty
      ? <EmptyNote>{artistsEmpty}</EmptyNote>
      : null;

  // If both empty + no empty-state copy provided, nothing renders.
  if (!tracksCol && !artistsCol) return null;

  return (
    <div className="mc-split">
      {tracksCol && (
        <div className="mc-col">
          <div className="mc-col-label">{tracksLabel}</div>
          {tracksCol}
        </div>
      )}
      {artistsCol && (
        <div className="mc-col">
          <div className="mc-col-label">{artistsLabel}</div>
          {artistsCol}
        </div>
      )}
    </div>
  );
}

function EmptyNote({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        padding: 10,
        borderRadius: 10,
        background: 'var(--surface-2)',
        border: '1px dashed var(--line-strong)',
        fontSize: 12,
        color: 'var(--ink-3)',
        lineHeight: 1.4,
      }}
    >
      {children}
    </div>
  );
}
